import { ArrowLeftIcon, Box, CloseIcon, HStack, Icon, Input, InputField, InputIcon, InputSlot, Pressable, Typography, VStack } from "components/ui";
import { H2, Text } from "components/ui";
import React, { FC, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Modal, ScrollView, View, useWindowDimensions, ViewProps, NativeMethods } from "react-native";
import { EquipmentType, useGetEquipmentEquipmenttypeQuery } from "store/abcoApi";
import { SelectBaseProps, Value } from "types/select.types";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useDropdownComponent } from "utils/hooks/useDropdownComponent";
import { useNavigation, usePathname } from "expo-router";
import { router } from "expo-router";
import { Textbox } from "components/form";
import { SearchIcon } from "lucide-react-native";
import EquipmentTypeLabel from "./EquipmentTypeLabel";

interface EquipmentTypeSelectProps extends SelectBaseProps {
	value?: EquipmentType[];
	label?: string;
	isFluid?: boolean;
	isMulti?: boolean;
	placeholder?: string;
	error?: string;
	disabled?: boolean;
}

export const EquipmentTypeSelect: FC<EquipmentTypeSelectProps> = ({ onSelect, value = [], placeholder = "Select Equipment Type...", label, isFluid = true, isMulti = true, error, disabled = false }: EquipmentTypeSelectProps) => {
	const { data: equipmentTypes, isLoading, error: queryError, isFetching } = useGetEquipmentEquipmenttypeQuery();
	const [searchTerm, setSearchTerm] = useState<string>("");
	const { setDropdownComponent } = useDropdownComponent();
	const [selectedItems, setSelectedItems] = useState<EquipmentType[]>(value);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownButtonRef = useRef<View & NativeMethods>(null);
	const dropdownRef = useRef<ScrollView>(null);
	const firstElementRef = useRef<View & NativeMethods>(null);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [buttonWidth, setButtonWidth] = useState(0);
	const isMobile = useIsMobile();
	const pathname = usePathname();
	const [originalPathname, setOriginalPathname] = useState<string>("");

	// Update internal state when external value changes
	useEffect(() => {
		if (value) {
			setSelectedItems(value);
		}
	}, [value]);

	// Memoize filtered equipment types
	const filteredEquipmentTypes = useMemo(() => {
		if (!equipmentTypes) return [];
		return equipmentTypes.filter((type) => {
			const matchesSearch = type.description?.toLowerCase().includes(searchTerm.toLowerCase());
			const notSelected = !selectedItems.find((selected) => selected.id === type.id);
			return matchesSearch && notSelected;
		});
	}, [equipmentTypes, searchTerm, selectedItems]);

	const open = useCallback(() => {
		if (disabled) return;

		if (isMobile) {
			setIsOpen(true);
			setOriginalPathname(pathname);
			router.push({ pathname: "/dropdown" });
		} else {
			// @ts-ignore
			dropdownButtonRef.current?.measure((fx, fy, w, h, px, py) => {
				setPosition({ x: px, y: py + h });
				setButtonWidth(w);
				setIsOpen(true);
			});
		}
	}, [disabled, isMobile, pathname]);

	const close = useCallback(() => {
		setIsOpen(false);
		if (isMobile) {
			router.navigate({ pathname: originalPathname });
		}
	}, [isMobile, originalPathname]);

	const handleSelect = useCallback(
		(equipmentType: EquipmentType) => {
			let newSelectedItems: EquipmentType[];

			if (isMulti) {
				// Check if item is already selected
				const isAlreadySelected = selectedItems.some((item) => item.id === equipmentType.id);
				if (isAlreadySelected) {
					newSelectedItems = selectedItems.filter((item) => item.id !== equipmentType.id);
				} else {
					newSelectedItems = [...selectedItems, equipmentType];
				}
			} else {
				newSelectedItems = [equipmentType];
			}

			// Update internal state first
			setSelectedItems(newSelectedItems);

			// Then notify parent component
			if (onSelect) {
				onSelect(newSelectedItems, 0);
			}

			// Only close if not in multi-select mode
			if (!isMulti) {
				close();
			}
		},
		[isMulti, selectedItems, onSelect, close],
	);

	const removeEquipmentType = useCallback(
		(equipmentTypeToRemove: EquipmentType) => {
			const newSelectedItems = selectedItems.filter((type) => type.id !== equipmentTypeToRemove.id);
			setSelectedItems(newSelectedItems);
			if (onSelect) {
				onSelect(newSelectedItems, 0);
			}
		},
		[selectedItems, onSelect],
	);

	const clearSelection = useCallback(() => {
		setSelectedItems([]);
		if (onSelect) {
			onSelect([], 0);
		}
	}, [onSelect]);

	const renderSelectedItems = useCallback(() => {
		return selectedItems.map((equipmentType) => (
			<HStack
				key={equipmentType.id}
				sx={{
					backgroundColor: "$neutral25",
					marginHorizontal: label ? 6 : 0,
					marginVertical: label ? 5 : 0,
				}}
			>
				<EquipmentTypeLabel description={equipmentType.description ?? ""} />
				<Pressable
					sx={{
						borderColor: "$neutral25",
						marginRight: 4,
						justifyContent: "center",
						alignItems: "center",
						paddingVertical: 2,
						borderTopRightRadius: 4,
						borderBottomRightRadius: 4,
					}}
					onPress={() => removeEquipmentType(equipmentType)}
					accessibilityLabel={`Remove ${equipmentType.description}`}
				>
					<CloseIcon w={12} h={12} />
				</Pressable>
			</HStack>
		));
	}, [selectedItems, label, removeEquipmentType]);

	const renderDropdownContent = useCallback(() => {
		if (isLoading || isFetching) {
			return (
				<Box padding={16}>
					<Text>Loading equipment types...</Text>
				</Box>
			);
		}

		if (queryError) {
			return (
				<Box padding={16}>
					<Text color="$errordark">Error loading equipment types. Please try again.</Text>
				</Box>
			);
		}

		if (filteredEquipmentTypes.length === 0) {
			return (
				<Box padding={16}>
					<Text>No equipment types found.</Text>
				</Box>
			);
		}

		return filteredEquipmentTypes.map((equipmentType) => (
			<Pressable
				key={equipmentType.id}
				onPress={() => handleSelect(equipmentType)}
				style={{
					padding: 10,
					borderBottomWidth: 1,
					borderBottomColor: "#e6e6e6",
					backgroundColor: selectedItems.some((item) => item.id === equipmentType.id) ? "#f5f5f5" : "transparent",
				}}
				accessibilityLabel={`Select ${equipmentType.description}`}
			>
				<Text>{equipmentType.description}</Text>
			</Pressable>
		));
	}, [isLoading, isFetching, queryError, filteredEquipmentTypes, handleSelect, selectedItems]);

	// Mobile dropdown component
	useEffect(() => {
		if (isMobile && isOpen) {
			setDropdownComponent(
				<ScrollView ref={dropdownRef} stickyHeaderIndices={[0]} style={{ flex: 1 }}>
					<Box
						backgroundColor="$primary"
						paddingHorizontal={16}
						paddingVertical={18}
						style={{
							position: "sticky",
							top: 0,
							zIndex: 1000,
						}}
					>
						<HStack space="xl" alignItems="center" mx="$0.5">
							<Pressable onPress={close} accessibilityLabel="Go back">
								<Icon color="$basicwhite" as={ArrowLeftIcon} m="$2" size="md" />
							</Pressable>
							<Input
								flex={1}
								backgroundColor="$basicwhite"
								variant="outline"
								borderRadius={12}
								borderColor="$borderBlue"
								size="md"
								px="$0"
								py="$3"
								isDisabled={false}
								isInvalid={false}
								isReadOnly={false}
								style={{ zIndex: 100 }}
								isFocused={true}
								isHovered={true}
							>
								<InputSlot>
									<InputIcon as={SearchIcon} ml="$4" color="$basicblack" />
								</InputSlot>
								<InputField fontSize={"$h5desktop"} backgroundColor="white" placeholder="Search equipment types..." value={searchTerm} onChangeText={setSearchTerm} color="$basicblack" placeholderTextColor={"#cccccc"} />
								{searchTerm.length > 0 && (
									<InputSlot>
										<Pressable onPress={() => setSearchTerm("")} accessibilityLabel="Clear search">
											<InputIcon as={CloseIcon} mr="$4" color="$basicblack" />
										</Pressable>
									</InputSlot>
								)}
							</Input>
						</HStack>
					</Box>
					<Box paddingBottom={100}>{renderDropdownContent()}</Box>
				</ScrollView>,
			);
		}
	}, [searchTerm, isMobile, isOpen, renderDropdownContent]);

	return (
		<>
			<Pressable
				ref={dropdownButtonRef}
				onPress={open}
				disabled={disabled}
				sx={{
					cursor: "default",
					maxWidth: isFluid ? "auto" : isMobile ? "auto" : 525,
					width: "$full",
					paddingHorizontal: 12,
					justifyContent: "center",
					paddingVertical: selectedItems.length > 0 ? 8 : 12,
					borderColor: error ? "$errordark" : "#b3b3b3",
					background: "$basicwhite",
					borderWidth: 1,
					height: isMobile ? "auto" : "$full",
					borderRadius: 12,
					maxHeight: isFluid ? "auto" : 50,
					position: "relative",
					opacity: disabled ? 0.6 : 1,
				}}
				accessibilityLabel={label ? `${label} dropdown` : "Equipment type dropdown"}
				accessibilityHint={disabled ? "Dropdown is disabled" : "Double tap to open dropdown"}
			>
				{label && (
					<Typography
						sx={{
							top: isMobile ? -11 : -1,
							position: "absolute",
							zIndex: 1,
							lineHeight: 1.5,
							left: 11,
							background: "white",
							paddingLeft: 6,
							fontSize: 13,
							fontWeight: 700,
							paddingRight: 6,
							color: error ? "$errordark" : "$basicblack",
						}}
					>
						{label}:
					</Typography>
				)}
				<HStack flexWrap="wrap" gap={6}>
					{selectedItems.length > 0 ? (
						renderSelectedItems()
					) : (
						<Text fontSize={14} color="$neutral200">
							{placeholder}
						</Text>
					)}
				</HStack>
				{error && (
					<Text color="$errordark" fontSize={12} mt={1}>
						{error}
					</Text>
				)}
			</Pressable>
			{!isMobile && (
				<Modal onRequestClose={close} style={{ flex: 1, position: "relative" }} supportedOrientations={["portrait", "landscape"]} animationType="none" transparent={true} visible={isOpen}>
					<Pressable
						onPress={close}
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							bottom: 0,
							right: 0,
							cursor: "default",
						}}
					/>
					<ScrollView
						ref={dropdownRef}
						stickyHeaderIndices={[0]}
						style={{
							position: "absolute",
							top: position.y,
							left: position.x,
							width: buttonWidth,
							display: isOpen ? "flex" : "none",
							backgroundColor: "white",
							borderColor: "black",
							borderRadius: 12,
							zIndex: 9999,
							shadowColor: "#000",
							shadowOffset: {
								width: 0,
								height: 1,
							},
							shadowOpacity: 0.2,
							shadowRadius: 1.41,
							maxHeight: 300,
							elevation: 2,
						}}
					>
						<Box
							style={{
								width: "100%",
								borderWidth: 0,
								backgroundColor: "white",
								position: "sticky",
								top: 0,
								zIndex: 1000,
							}}
						>
							<Textbox placeholder="Search equipment types..." value={searchTerm} onChange={setSearchTerm} />
						</Box>
						{renderDropdownContent()}
					</ScrollView>
				</Modal>
			)}
		</>
	);
};
